// Pull apart trigger circles that land on the same spot on the timeline.
//
// Two triggers dated the same day resolve to the same x, so their circles draw exactly on
// top of one another. The label side alternates (see `computeVisibility`), so the pair does
// not read as "two triggers stacked" — it reads as ONE circle with a label coming out of its
// top and another out of its bottom, which is a shape the timeline never otherwise draws and
// which no user has a way to interpret. Nudging the circles apart costs a little date
// precision and buys the only thing that matters here: you can see there are two.
//
// The spread is deliberately small. At a typical zoom a dozen pixels is a day or two, and the
// anchor is already approximate for a ranged trigger (it sits at the window's mean), so the
// misplacement stays well inside the uncertainty the marker is drawing in the first place.

/** Circles closer than this are indistinguishable, so they get spread. */
export const COINCIDENT_PX = 8;

/** How far apart to push neighbours in a spread cluster. Tuned against `BASE_R` (13): wide
 *  enough that two discs read as two, narrow enough to stay inside a trigger's own date
 *  uncertainty. */
export const SPREAD_PX = 12;

/** How far right of a branch fork a circle must stay to be drawn (renderRow keeps only
 *  circles past the fork's x + 1). */
export const FORK_CLEAR_PX = 2;

/** The furthest a fan may spread a circle from its own x. A cluster whose fan would move a
 *  member further is a pile and stays on its own x: fanning a dense run pushes triggers
 *  weeks off their dates, and because clusters form in screen px, zooming regroups the run
 *  and moves its circles. */
export const MAX_FAN_SHIFT_PX = 2 * SPREAD_PX;

/**
 * New x positions for a row of trigger circles, with coincident ones pulled apart.
 *
 * Chains are grouped transitively — three triggers each 6px from the next are one cluster of
 * three, not two overlapping pairs — so a dense day comes out evenly fanned rather than
 * partially stacked.
 *
 * `anchorId` names a point that MUST keep its exact x: the fork event a branch hangs off.
 * Its connector and corner are drawn from the fork date independently of this layout, so
 * moving the circle would leave the branch line pointing at empty space. When a cluster
 * contains it, the whole cluster is translated to put it back rather than dropping the
 * spread — the others still separate, and the branch stays joined to its node.
 *
 * `maxX` is a right edge no fanned circle may cross: the row's forecast line, where the
 * marker hangs. A cluster sitting on it would otherwise fan half its members past it. After the fan settles,
 * one right-to-left sweep caps the rightmost circle at the edge and pushes each neighbour it
 * crowds `gap` further left — a sweep, not a shift, so it can never re-create a collision.
 *
 * `maxShift` (default `MAX_FAN_SHIFT_PX`) bounds the fan: a cluster whose fan would carry any
 * member further than this from its own x is a pile: every member goes back to its own x, and
 * any cluster that later touches it joins it. The edge sweep is not bounded by it.
 *
 * @param {ReadonlyArray<{id?: string, sx: number}>} points in render order
 * @param {{gap?: number, threshold?: number, anchorId?: string|null, maxX?: number|null,
 *   maxShift?: number}} [opts]
 * @returns {number[]} one x per input point, in the same order
 */
export function spreadCoincidentX(points, opts = {}) {
  const gap = opts.gap ?? SPREAD_PX;
  const threshold = opts.threshold ?? COINCIDENT_PX;
  const anchorId = opts.anchorId ?? null;
  const maxX = Number.isFinite(opts.maxX) ? opts.maxX : null;
  const maxShift = opts.maxShift ?? MAX_FAN_SHIFT_PX;

  const list = Array.isArray(points) ? points : [];
  const out = list.map((p) => (Number.isFinite(p?.sx) ? p.sx : NaN));
  if (list.length < 2) return out;
  const own = [...out];
  const pile = new Set();

  // Only finite positions take part; a NaN has no place on the line to cluster around.
  const order = list
    .map((p, i) => i)
    .filter((i) => Number.isFinite(out[i]))
    .sort((a, b) => out[a] - out[b]);

  /** Fan every cluster once, over the CURRENT positions. Returns whether anything moved. */
  const pass = () => {
    let moved = false;
    let cluster = [];
    const flush = () => {
      if (cluster.length > 1) {
        // Centre the fan on the cluster's own midpoint, so the group stays where the
        // triggers actually are instead of drifting toward whichever sorted first.
        const mid = cluster.reduce((sum, i) => sum + out[i], 0) / cluster.length;
        const span = (cluster.length - 1) * gap;
        const placed = cluster.map((_, k) => mid - span / 2 + k * gap);
        // Put the anchor back on its true x, carrying the rest of the cluster with it.
        const anchorAt = anchorId == null ? -1 : cluster.findIndex((i) => list[i].id === anchorId);
        const shift = anchorAt >= 0 ? out[cluster[anchorAt]] - placed[anchorAt] : 0;
        // Measured on the centred fan: carrying an anchored cluster onto its anchor is a shift
        // of the whole group, not spread, and must not collapse a small group at a fork.
        const isPile = cluster.some(
          (i, k) => pile.has(i) || Math.abs(placed[k] - own[i]) > maxShift,
        );
        cluster.forEach((i, k) => {
          // A pile goes back to its own x — undoing any fan an earlier pass gave part of it —
          // and stays there, absorbing whatever it later touches.
          if (isPile) pile.add(i);
          const next = isPile ? own[i] : placed[k] + shift;
          if (next !== out[i]) moved = true;
          out[i] = next;
        });
      }
      cluster = [];
    };
    // Re-sorted every pass: a fan can reorder nothing (it preserves order within a cluster)
    // but CAN carry a cluster into its neighbour, and the merge below only sees that if the
    // walk is over current positions.
    const byX = [...order].sort((a, b) => out[a] - out[b]);
    for (const i of byX) {
      if (cluster.length && out[i] - out[cluster[cluster.length - 1]] > threshold) flush();
      cluster.push(i);
    }
    flush();
    return moved;
  };

  // ITERATE TO A FIXED POINT. One pass is not enough: clusters are formed from positions as
  // they stand, so fanning one can push its outermost member within `threshold` of the next
  // cluster along — `[0, 7, 16]` at the defaults formed `[0,7]` + `[16]` and laid out
  // `[-2.5, 9.5, 16]`, leaving 6.5px, which is the very collision this exists to prevent.
  // Each pass either merges neighbouring clusters or changes nothing, and a merge strictly
  // reduces the cluster count, so this converges; the bound is belt and braces.
  for (let i = 0; i < list.length && pass(); i++) {
    /* keep fanning until the layout stops moving */
  }
  // A pile holding the branch fork puts members dated on the fork's x onto it, and a branch
  // row draws only what lies right of its fork. Stack them just right of it instead.
  const forkAt = anchorId == null ? -1 : list.findIndex((p) => p?.id === anchorId);
  if (forkAt >= 0 && pile.has(forkAt)) {
    for (const i of pile) {
      if (i !== forkAt && own[i] >= own[forkAt]) {
        out[i] = Math.max(own[i], own[forkAt] + FORK_CLEAR_PX);
      }
    }
  }
  if (maxX != null) {
    // Walk right to left from the edge, moving ONLY the circles the edge displaces: each one
    // moved lands `gap` left of the last, and the walk stops at the first circle already
    // clear of it — so triggers nowhere near the marker keep exactly the x the fan gave them.
    //
    // The branch fork keeps its exact x (see `anchorId`) and nothing is moved across it: a
    // branch row draws only what lies RIGHT of its fork, so carrying a circle past it would
    // hide a real trigger. When the room between fork and edge is short of `gap` per circle,
    // the displaced circles share it evenly instead — tighter, but all still drawn.
    const fork = anchorId == null ? -1 : (order.find((i) => list[i].id === anchorId) ?? -1);
    const chain = [];
    let limit = maxX;
    for (const i of [...order].sort((a, b) => out[b] - out[a])) {
      if (i === fork || out[i] <= limit) break;
      chain.push(i);
      limit -= gap;
    }
    const floor = fork >= 0 ? out[fork] + FORK_CLEAR_PX : -Infinity;
    const leftmost = maxX - (chain.length - 1) * gap;
    chain.forEach((i, k) => {
      // A fork ON the marker leaves no room right of it: anything there is after the
      // resolution, so it goes on the fork — which the branch row's filter then leaves out.
      if (floor > maxX) out[i] = out[fork];
      else if (leftmost >= floor) out[i] = maxX - k * gap;
      else {
        const hi = Math.max(maxX, floor);
        out[i] = chain.length === 1 ? hi : hi - (k * (hi - floor)) / (chain.length - 1);
      }
    });
  }
  return out;
}
